import { inputRules as inputRulesPlugin, wrappingInputRule, textblockTypeInputRule, } from 'prosemirror-inputrules';
import { EditorState, Plugin as ProseMirrorPlugin, PluginKey as ProseMirrorPluginKey, } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { keymap } from 'prosemirror-keymap';
import { SchemaType, DOMType, ChildrenDOMType, simpleEvents, } from './constants.js';
import { markInputRule, createEnterHandlerByInputRule, getMarkType, getNodeType, getSchemaType, getNodeFromHTML, } from './utils/index.js';
import { builtInSpec, createBuiltInProseMirrorPlugins, createBuiltInPlugins, } from './built-ins/index.js';
import { createSchemaFromPlugins } from './utils/schema.js';
import { createProseMirrorPluginByDecorate } from './utils/decorate.js';
import { createProseMirrorPluginByStyles } from './utils/styles.js';
import { truthy } from './utils/truthy.js';
import { throttle } from './utils/throttle.js';
import { sortUnique } from './utils/plugin.js';
import { patchDisplayNameIfMissing } from './utils/shortcut.js';
import { createToolbar, createPopoverToolbar } from './utils/toolbar.js';
import { Tracker, report } from './utils/tracker.js';
import { EditorCoreCommands } from './EditorCoreCommands.js';
import { DOMParser } from './utils/parser.js';
export { DOMType, ChildrenDOMType, ProseMirrorPlugin, ProseMirrorPluginKey };
export class Editor extends EditorCoreCommands {
    static create(config) {
        const editor = new Editor(config);
        return Object.assign(editor, editor.pluginCommands);
    }
    constructor(config) {
        super();
        this.config = config;
        this.pluginCommands = Object.create(null);
        this.isDestroyed = false;
        this.debug = () => () => { };
        this.nodeViews = {};
        this.inputRules = [];
        this.toolbarItemRenderersMap = {};
        this.toolbarElements = [];
        if (typeof config.debug === 'function') {
            this.debug = config.debug;
        }
        else if (config.debug) {
            this.debug =
                (namespace) => (...data) => console.log(namespace, ...data);
        }
        this.proseMirrorPlugins = createBuiltInProseMirrorPlugins(config);
        // plugin 优先级高的排在前面
        this.config.plugins = sortUnique([
            ...createBuiltInPlugins(),
            ...this.config.plugins.flatMap((p) => [p, ...(p.dependencies || [])]),
        ]);
        this.initPlugins();
        this.createSchema();
        this.setupPlugin();
        this.createView();
        this.setupToolbar();
        this.addCommonEvents();
        this.initTracker();
        this.emit('init');
    }
    getRootDOM() {
        return this.config.root;
    }
    getContentEditableRootDOM() {
        return this.getRootDOM().querySelector('[data-exeditor-root]');
    }
    getToolbarRootDOM() {
        return this.config.toolbarRoot;
    }
    /**
     * 从用户指定图标库获取获取图标 svg
     * 对于分号分隔的多个名字，返回非空的第一个
     */
    useIcon(name) {
        var _a;
        for (const n of name.split(/\s*;\s*/)) {
            if ((_a = this.config.iconPack) === null || _a === void 0 ? void 0 : _a[n]) {
                return this.config.iconPack[n];
            }
        }
        return undefined;
    }
    /**
     * 按用户配置的语言词库包翻译字符串
     */
    t(token, fallback) {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.config.languagePack) === null || _a === void 0 ? void 0 : _a[token]) !== null && _b !== void 0 ? _b : fallback) !== null && _c !== void 0 ? _c : token;
    }
    destroy() {
        var _a, _b, _c;
        this.emit('beforedestroy');
        (_a = this.view) === null || _a === void 0 ? void 0 : _a.destroy();
        (_c = (_b = this.config.theme) === null || _b === void 0 ? void 0 : _b.destroy) === null || _c === void 0 ? void 0 : _c.call(_b);
        this.removeAllListeners();
        this.isDestroyed = true;
    }
    initPlugins() {
        this.config.plugins.forEach((plug) => {
            var _a;
            var _b, _c;
            if (!plug.name) {
                throw new Error(`An exeditor plugin must have a name!`);
            }
            if (typeof this[plug.name] !== 'undefined') {
                throw new Error(`Do not name your exeditor plugin as "${plug.name}" which is a reserved word.`);
            }
            (_a = plug.initEditor) === null || _a === void 0 ? void 0 : _a.call(plug, this);
            const { renderToolbarItem } = plug;
            if (typeof renderToolbarItem === 'function') {
                (_b = this.toolbarItemRenderersMap)[_c = plug.name] || (_b[_c] = renderToolbarItem.bind(plug));
            }
            else if (renderToolbarItem) {
                Object.keys(renderToolbarItem).forEach((toolbarItemName) => {
                    var _a;
                    (_a = this.toolbarItemRenderersMap)[toolbarItemName] || (_a[toolbarItemName] = renderToolbarItem[toolbarItemName].bind(plug));
                });
            }
        });
    }
    createSchema() {
        this.schemaInstance = createSchemaFromPlugins(this.config.plugins, [
            builtInSpec,
        ]);
    }
    getInputRule(rule, plug) {
        var _a;
        switch (getSchemaType((_a = plug.spec) === null || _a === void 0 ? void 0 : _a.type)) {
            case SchemaType.MARK: {
                const markType = getMarkType(plug.name, this.schemaInstance);
                return markInputRule(rule, markType);
            }
            case SchemaType.NODE: {
                const nodeType = getNodeType(plug.name, this.schemaInstance);
                return (nodeType.isTextblock ? textblockTypeInputRule : wrappingInputRule)(rule.match, nodeType, rule.getAttrs);
            }
        }
    }
    setupPlugin() {
        const shortcutsList = [];
        this.config.plugins.forEach((plug) => {
            var _a;
            if (plug.capabilities) {
                const plugCapabilities = plug.capabilities();
                if (plugCapabilities.commands) {
                    this.pluginCommands[plug.name] = plugCapabilities.commands;
                }
                this.proseMirrorPlugins.push(...((plugCapabilities === null || plugCapabilities === void 0 ? void 0 : plugCapabilities.prosemirrorPlugins) || []));
                const enterHandlerForInputRules = [];
                if (plugCapabilities.inputRules) {
                    const normalizedInputRules = plugCapabilities.inputRules.map((r) => ({
                        getAttrs: (matches) => matches.groups,
                        ...(r instanceof RegExp ? { match: r } : r),
                    }));
                    for (const r of normalizedInputRules) {
                        const rule = this.getInputRule(r, plug);
                        this.inputRules.push(rule);
                        if (r.captureEnter) {
                            enterHandlerForInputRules.push(createEnterHandlerByInputRule(rule));
                        }
                    }
                }
                const shortcuts = enterHandlerForInputRules.length
                    ? {
                        ...plugCapabilities.shortcuts,
                        Enter: [
                            (_a = plugCapabilities.shortcuts) === null || _a === void 0 ? void 0 : _a.Enter,
                            ...enterHandlerForInputRules,
                        ]
                            .filter(truthy)
                            .flat(1),
                    }
                    : plugCapabilities.shortcuts;
                if (shortcuts) {
                    patchDisplayNameIfMissing(shortcuts, plug);
                    shortcutsList.push(shortcuts);
                }
                if (plugCapabilities.callbacks) {
                    this.proseMirrorPlugins.push(new ProseMirrorPlugin({
                        props: Object.keys(plugCapabilities.callbacks).reduce((ret, key) => ({
                            ...ret,
                            [key]: ((_view, ...rest) => {
                                const cb = plugCapabilities.callbacks[key];
                                return cb(...rest);
                            }),
                        }), {}),
                    }));
                }
                if (plugCapabilities.handleDOMEvents) {
                    this.proseMirrorPlugins.push(new ProseMirrorPlugin({
                        props: {
                            handleDOMEvents: plugCapabilities.handleDOMEvents,
                        },
                    }));
                }
                if (plugCapabilities.transformHTML) {
                    const newHook = plugCapabilities.transformHTML;
                    const oldHook = this.transformHTMLHook;
                    this.transformHTMLHook = (html, event) => newHook(oldHook(html, event), event);
                }
                if (plugCapabilities.decorate) {
                    this.proseMirrorPlugins.push(createProseMirrorPluginByDecorate(this, plug.name, plugCapabilities.decorate));
                }
                if (plugCapabilities.styles) {
                    this.proseMirrorPlugins.push(createProseMirrorPluginByStyles(plug.name, plugCapabilities.styles));
                }
            }
            if (plug.display) {
                this.debug('core:deprecated')(`${plug.name} uses deprecated API display()`);
                this.nodeViews[plug.name] = (node, view, getPos, decorations) => plug.display({
                    node,
                    attributes: node.attrs,
                    view,
                    getPos: getPos,
                    decorations,
                });
            }
            if (plug.editView) {
                this.nodeViews[plug.name] = (node, view, getPos) => {
                    const ret = plug.editView(node, getPos, view);
                    if (ret instanceof Node) {
                        return { dom: ret };
                    }
                    const { dom } = ret;
                    if (!(dom instanceof Element)) {
                        return ret;
                    }
                    // prosemirror 的 update 可能会被尝试传入不同的 node type
                    // https://prosemirror.net/docs/ref/#view.NodeView.update (possibly of a different type)
                    // 但实践中几乎不会有插件去处理多种 type，这里自动拒绝这类尝试
                    const oldUpdate = ret.update;
                    if (oldUpdate) {
                        ret.update = function update(newNode, ...rest) {
                            if (newNode.type !== node.type && !ret.handleMultipleTypes) {
                                return false;
                            }
                            return oldUpdate.call(ret, newNode, ...rest);
                        };
                    }
                    return ret;
                };
            }
        });
        this.proseMirrorPlugins.push(new ProseMirrorPlugin({
            props: {
                transformPastedHTML: (html) => {
                    const result = this.transformHTMLHook(html, this.nativePastedEvent);
                    this.nativePastedEvent = undefined;
                    return result;
                },
            },
        }));
        this.useShortcuts(shortcutsList);
    }
    useShortcuts(shortcutsList) {
        // 倒转，让优先级低的先入 chain，优先级高的在 chain 的头端
        this.keymaps = shortcutsList
            .reverse()
            .reduce((ret, shortcuts) => {
            for (const [key, fn] of Object.entries(shortcuts)) {
                if (!ret[key]) {
                    ret[key] = this.chainShortCuts(key, fn);
                }
                else {
                    ret[key] = this.chainShortCuts(key, fn, ret[key]);
                }
            }
            return ret;
        }, {});
        this.proseMirrorPlugins.unshift(keymap(this.keymaps));
    }
    createView() {
        this.config.root.innerHTML = '';
        this.config.root.addEventListener('paste', (evt) => {
            this.nativePastedEvent = evt;
        }, true);
        const { theme } = this.config;
        this.view = new EditorView(this.config.root, {
            dispatchTransaction: this.dispatchTransaction.bind(this),
            state: EditorState.create({
                doc: getNodeFromHTML(this.transformHTMLHook(this.config.initialContent || ''), this.schemaInstance),
                plugins: [
                    ...this.proseMirrorPlugins,
                    inputRulesPlugin({ rules: this.inputRules }),
                    theme &&
                        new ProseMirrorPlugin({
                            props: {
                                attributes: {
                                    class: `ExEditor-${theme.namespace}`,
                                },
                            },
                        }),
                    (theme === null || theme === void 0 ? void 0 : theme.styles) &&
                        createProseMirrorPluginByStyles(theme.namespace, theme.styles),
                    new ProseMirrorPlugin({
                        props: {
                            attributes: this.config.attributes,
                            scrollMargin: this.config.scrollMargin,
                            scrollThreshold: this.config.scrollThreshold,
                        },
                    }),
                ].filter(truthy),
            }),
            domParser: DOMParser.fromSchema(this.schemaInstance),
            nodeViews: this.nodeViews,
        });
    }
    setupToolbar() {
        var _a;
        const { toolbarRoot, toolbar } = this.config;
        const getItemRenderer = (item) => {
            const renderer = this.toolbarItemRenderersMap[item];
            if (!renderer) {
                console.warn(`Cannot render unknown toolbar item ${item}`);
                return;
            }
            return (context, params) => {
                const eltItem = renderer(context, params);
                eltItem.addEventListener('click', () => {
                    report('toolbar_click', {
                        page_url: window.location.href,
                        config: JSON.stringify({ name: item, in: context.in }),
                    });
                });
                return eltItem;
            };
        };
        const toolbarContext = {
            theme: (_a = this.config.theme) === null || _a === void 0 ? void 0 : _a.namespace,
            getItemRenderer,
            editor: this,
        };
        const getDefault = (input) => {
            if (typeof input === 'string' || Array.isArray(input)) {
                return input;
            }
            return undefined;
        };
        const getSingle = (input, where) => {
            if (!input || typeof input === 'string' || Array.isArray(input)) {
                return undefined;
            }
            return input[where];
        };
        const staticToolbar = getSingle(toolbar, 'static') || getDefault(toolbar);
        const root = toolbarRoot;
        if (staticToolbar && root) {
            root.innerHTML = '';
            const toolbarEl = createToolbar(staticToolbar, toolbarContext);
            root.appendChild(toolbarEl);
        }
        const popoverToolbar = getSingle(toolbar, 'popover');
        if (popoverToolbar) {
            const popoverToolbarEl = createPopoverToolbar(popoverToolbar, toolbarContext, root);
            this.toolbarElements.push(popoverToolbarEl);
        }
    }
    addCommonEvents() {
        const { dom } = this.view;
        const simpleEventsHandlerMap = simpleEvents.reduce((ret, key) => ({
            ...ret,
            [key]: () => this.emit(key),
        }), {});
        Object.keys(simpleEventsHandlerMap).forEach((key) => {
            dom.addEventListener(key, simpleEventsHandlerMap[key]);
        });
        const handleScroll = () => {
            this.emit('scroll');
        };
        const handleScrollDebounce = throttle(handleScroll, 15);
        dom.addEventListener('scroll', handleScrollDebounce);
        let oldRect = dom.getBoundingClientRect();
        const handleResize = (rect) => {
            this.emit('resize', {
                oldRect,
                rect,
            });
            oldRect = rect;
        };
        const handleResizeDebounce = throttle(handleResize, 15);
        const rsob = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === dom && entry.contentRect) {
                    handleResizeDebounce(entry.contentRect);
                }
            });
        });
        rsob.observe(dom);
        const allEditorEls = [dom, ...this.toolbarElements];
        const handleBlur = (ev) => {
            const relatedEl = ev.relatedTarget;
            const isFocusOthers = relatedEl && this.toolbarElements.some((el) => el.contains(relatedEl));
            if (!isFocusOthers) {
                this.emit('blur', ev);
            }
        };
        allEditorEls.forEach((el) => {
            el.addEventListener('blur', handleBlur);
        });
        this.on('beforedestroy', () => {
            Object.keys(simpleEventsHandlerMap).forEach((key) => {
                dom.removeEventListener(key, simpleEventsHandlerMap[key]);
            });
            dom.removeEventListener('scroll', handleScroll);
            rsob.unobserve(dom);
            allEditorEls.forEach((el) => {
                el.removeEventListener('blur', handleBlur);
            });
        });
    }
    chainShortCuts(key, ...commands) {
        const flattedShortCuts = commands.flat();
        const chainedShortCut = (state, dispatch, view) => flattedShortCuts.some((c) => {
            const dispatchAndScroll = dispatch
                ? (tr) => dispatch(tr.scrollIntoView())
                : undefined;
            const handled = c(state, dispatchAndScroll, view) !== false;
            if (handled && c.name !== chainedShortCut.name) {
                this.debug('core:shortcuts')(`${key} is handled by`, c.displayName || c.name);
            }
            return handled;
        });
        return chainedShortCut;
    }
    initTracker() {
        const beaconAction = this.config.BeaconAction;
        if (beaconAction === false) {
            return;
        }
        setTimeout(() => {
            new Tracker({
                beaconAction: beaconAction === true ? undefined : beaconAction,
            });
            this.reportInfo();
        });
    }
    reportInfo() {
        const rect = this.config.root.getBoundingClientRect();
        const plugNameList = this.config.plugins.map((plug) => plug.name).join(',');
        const toolbar = JSON.stringify(this.config.toolbar || '');
        report('editor_init', {
            page_url: window.location.href,
            size: `${rect.width}x${rect.height}`,
            plugin_list: plugNameList,
            toolbar,
        });
    }
}
//# sourceMappingURL=Editor.js.map